import React, { useState, useEffect } from "react";

const PROD_APP_ID = "16e74317-1843-41f7-a5e4-6c249be02289";
const DEV_APP_ID = "caf0e24c-1bee-4ae2-88a2-604ab5723d77"; // localhost OneSignal app

const FUNCTION_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001/send"
    : "https://us-central1-doctor12.cloudfunctions.net/sendPushNotification";

const ONESIGNAL_APP_ID =
  window.location.hostname === "localhost" ? DEV_APP_ID : PROD_APP_ID;

const isPlaceholder = ONESIGNAL_APP_ID === "YOUR_DEV_APP_ID_HERE";

export default function PushNotification() {
  const [isReady, setIsReady] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState(Notification.permission);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [log, setLog] = useState([]);
  const [notifTitle, setNotifTitle] = useState("");
  const [notifBody, setNotifBody] = useState("");
  const [sending, setSending] = useState(false);

  const addLog = (msg, type = "info") => {
    const time = new Date().toLocaleTimeString();
    setLog((prev) => [...prev, { msg, type, time }]);
  };

  useEffect(() => {
    if (isPlaceholder) return;

    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.init({
          appId: ONESIGNAL_APP_ID,
          allowLocalhostAsSecureOrigin: true,
          notifyButton: { enable: false },
        });

        setIsReady(true);
        setIsSubscribed(!!OneSignal.User.PushSubscription.optedIn);

        const subId = OneSignal.User.PushSubscription.id;
        if (subId) setSubscriptionId(subId);

        OneSignal.User.PushSubscription.addEventListener("change", (event) => {
          const opted = !!event.current.optedIn;
          setIsSubscribed(opted);
          const id = event.current.id;
          if (id) setSubscriptionId(id);
          addLog(opted ? "Subscribed!" : "Unsubscribed", opted ? "ok" : "info");
        });

        addLog("OneSignal ready", "ok");
      } catch (err) {
        addLog("Init error: " + err.message, "error");
      }
    });
  }, []);

  const handleSubscribe = async () => {
    if (!isReady) {
      addLog("OneSignal not ready yet", "error");
      return;
    }
    setLoading(true);
    try {
      await window.OneSignal.Notifications.requestPermission();
      const perm = Notification.permission;
      setPermission(perm);

      if (perm !== "granted") {
        addLog("Permission denied", "error");
        return;
      }

      await window.OneSignal.User.PushSubscription.optIn();

      // wait a tick for the subscription ID to populate
      await new Promise((r) => setTimeout(r, 1000));
      const id = window.OneSignal.User.PushSubscription.id;
      if (id) {
        setSubscriptionId(id);
        addLog("Got subscription ID!", "ok");
      } else {
        addLog("Subscribed (ID will appear shortly)", "ok");
      }
    } catch (err) {
      addLog("Error: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!isReady) return;
    setLoading(true);
    try {
      await window.OneSignal.User.PushSubscription.optOut();
      setSubscriptionId(null);
      addLog("Unsubscribed", "info");
    } catch (err) {
      addLog("Error: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!subscriptionId) return;
    navigator.clipboard.writeText(subscriptionId);
    addLog("Subscription ID copied!", "ok");
  };

  const handleSend = async (target) => {
    if (!notifTitle.trim() && !notifBody.trim()) {
      addLog("Enter a title or message first", "error");
      return;
    }
    setSending(true);
    try {
      if (target === "me" && !subscriptionId) {
        addLog("No subscription ID — subscribe first", "error");
        setSending(false);
        return;
      }

      const res = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: notifTitle,
          body: notifBody,
          target,
          subscriptionId,
          appId: ONESIGNAL_APP_ID,
        }),
      });

      const data = await res.json();
      if (data.errors && data.errors.length) {
        addLog("Send error: " + data.errors.join(", "), "error");
      } else {
        addLog(
          `Sent to ${target === "me" ? "you" : "all"} (id: ${data.id?.slice(0, 8)}…)`,
          "ok"
        );
      }
    } catch (err) {
      addLog("Send failed: " + err.message, "error");
    } finally {
      setSending(false);
    }
  };

  const permColor = {
    granted: { bg: "#dcfce7", color: "#166534", border: "#86efac" },
    denied: { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
    default: { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" },
  }[permission] || { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };

  if (isPlaceholder) {
    return (
      <div style={s.page}>
        <div style={s.container}>
          <div style={s.warning}>
            <strong>Localhost detected</strong> — OneSignal App IDs are
            domain-locked. To test on localhost, create a second OneSignal app
            at{" "}
            <a
              href="https://app.onesignal.com"
              target="_blank"
              rel="noreferrer"
            >
              app.onesignal.com
            </a>{" "}
            with Site URL <code>http://localhost:3000</code>, then replace{" "}
            <code>YOUR_DEV_APP_ID_HERE</code> in PushNotification.js.
            <br />
            <br />
            The subscription works on <strong>bokahit.se</strong> right now.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.header}>
          <h1 style={s.title}>Push Notifications</h1>
          <p style={s.subtitle}>OneSignal Web SDK v16</p>
        </div>

        <div style={s.card}>
          <div style={s.cardLabel}>SDK STATUS</div>
          <span
            style={{
              ...s.badge,
              background: isReady ? "#dcfce7" : "#f3f4f6",
              color: isReady ? "#166534" : "#374151",
              border: `1px solid ${isReady ? "#86efac" : "#d1d5db"}`,
            }}
          >
            {isReady ? "Ready" : "Loading..."}
          </span>
        </div>

        <div style={s.card}>
          <div style={s.cardLabel}>PERMISSION</div>
          <span
            style={{
              ...s.badge,
              background: permColor.bg,
              color: permColor.color,
              border: `1px solid ${permColor.border}`,
            }}
          >
            {permission}
          </span>
        </div>

        <div style={s.card}>
          <div style={s.cardLabel}>SUBSCRIPTION</div>
          <span
            style={{
              ...s.badge,
              background: isSubscribed ? "#dcfce7" : "#f3f4f6",
              color: isSubscribed ? "#166534" : "#374151",
              border: `1px solid ${isSubscribed ? "#86efac" : "#d1d5db"}`,
            }}
          >
            {isSubscribed ? "Subscribed" : "Not subscribed"}
          </span>
        </div>

        {subscriptionId && (
          <div style={s.card}>
            <div style={s.cardLabel}>SUBSCRIPTION ID (your token)</div>
            <div style={s.tokenBox}>{subscriptionId}</div>
            <button style={s.btnSecondary} onClick={handleCopy}>
              Copy ID
            </button>
          </div>
        )}

        <div style={s.card}>
          <div style={s.cardLabel}>ACTIONS</div>
          <div style={s.row}>
            {!isSubscribed ? (
              <button
                style={{
                  ...s.btnPrimary,
                  opacity: loading || !isReady ? 0.6 : 1,
                }}
                onClick={handleSubscribe}
                disabled={loading || !isReady}
              >
                {loading ? "Subscribing..." : "Subscribe"}
              </button>
            ) : (
              <button
                style={{ ...s.btnDanger, opacity: loading ? 0.6 : 1 }}
                onClick={handleUnsubscribe}
                disabled={loading}
              >
                {loading ? "..." : "Unsubscribe"}
              </button>
            )}
          </div>
        </div>

        <div style={s.card}>
          <div style={s.cardLabel}>SEND NOTIFICATION</div>
          <input
            style={s.input}
            placeholder="Title (optional)"
            value={notifTitle}
            onChange={(e) => setNotifTitle(e.target.value)}
          />
          <textarea
            style={s.textarea}
            placeholder="Message"
            value={notifBody}
            onChange={(e) => setNotifBody(e.target.value)}
            rows={3}
          />
          <div style={s.row}>
            <button
              style={{
                ...s.btnPrimary,
                opacity: sending ? 0.6 : 1,
              }}
              onClick={() => handleSend("me")}
              disabled={sending}
            >
              {sending ? "Sending..." : "Send to Me"}
            </button>
            <button
              style={{
                ...s.btnSend,
                opacity: sending ? 0.6 : 1,
              }}
              onClick={() => handleSend("all")}
              disabled={sending}
            >
              {sending ? "Sending..." : "Send to All"}
            </button>
          </div>
        </div>

        {log.length > 0 && (
          <div style={s.card}>
            <div style={s.cardLabel}>LOG</div>
            <div style={s.logBox}>
              {log.map((entry, i) => (
                <div key={i} style={s.logEntry}>
                  <span style={s.logTime}>[{entry.time}]</span>
                  <span
                    style={{
                      color:
                        entry.type === "ok"
                          ? "#16a34a"
                          : entry.type === "error"
                            ? "#dc2626"
                            : "#2563eb",
                    }}
                  >
                    {entry.msg}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f9fafb",
    padding: "2rem 1rem",
    fontFamily: "system-ui, sans-serif",
  },
  container: { maxWidth: 560, margin: "0 auto" },
  header: { marginBottom: "1.5rem" },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#111827",
    marginBottom: "0.25rem",
  },
  subtitle: { fontSize: "0.82rem", color: "#6b7280", fontFamily: "monospace" },
  warning: {
    background: "#fffbeb",
    border: "1px solid #fcd34d",
    borderRadius: 10,
    padding: "1rem 1.25rem",
    fontSize: "0.85rem",
    color: "#92400e",
    lineHeight: 1.6,
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1.25rem",
    marginBottom: "1rem",
  },
  cardLabel: {
    fontSize: "0.68rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: "#9ca3af",
    marginBottom: "0.75rem",
    fontFamily: "monospace",
  },
  badge: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    borderRadius: 20,
    fontSize: "0.8rem",
    fontFamily: "monospace",
    fontWeight: 600,
  },
  tokenBox: {
    background: "#f3f4f6",
    borderRadius: 8,
    padding: "0.75rem",
    fontSize: "0.7rem",
    color: "#065f46",
    wordBreak: "break-all",
    lineHeight: 1.7,
    marginBottom: "0.75rem",
    fontFamily: "monospace",
    border: "1px solid #d1fae5",
  },
  row: { display: "flex", gap: "0.75rem", flexWrap: "wrap" },
  btnPrimary: {
    padding: "0.6rem 1.4rem",
    borderRadius: 8,
    border: "none",
    background: "#4f46e5",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.9rem",
    cursor: "pointer",
  },
  btnDanger: {
    padding: "0.6rem 1.4rem",
    borderRadius: 8,
    border: "none",
    background: "#dc2626",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.9rem",
    cursor: "pointer",
  },
  btnSecondary: {
    padding: "0.5rem 1.1rem",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    fontWeight: 500,
    fontSize: "0.85rem",
    cursor: "pointer",
  },
  logBox: {
    background: "#f9fafb",
    borderRadius: 8,
    padding: "0.75rem",
    maxHeight: 160,
    overflowY: "auto",
    border: "1px solid #f3f4f6",
  },
  logEntry: {
    display: "flex",
    gap: "0.5rem",
    fontSize: "0.75rem",
    lineHeight: 1.9,
    fontFamily: "monospace",
  },
  logTime: { color: "#9ca3af", flexShrink: 0 },
  input: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    padding: "0.55rem 0.85rem",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    fontSize: "0.9rem",
    marginBottom: "0.6rem",
    fontFamily: "system-ui, sans-serif",
    outline: "none",
  },
  textarea: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    padding: "0.55rem 0.85rem",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    fontSize: "0.9rem",
    marginBottom: "0.75rem",
    fontFamily: "system-ui, sans-serif",
    resize: "vertical",
    outline: "none",
  },
  btnSend: {
    padding: "0.6rem 1.4rem",
    borderRadius: 8,
    border: "none",
    background: "#059669",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.9rem",
    cursor: "pointer",
  },
};
